import { useEffect } from "react";
import IowaCityHeroSection from "./components/HeroSection";
import IowaCityStickyNav from "./components/StickyNav";
import IowaCityProcessingTimeSection from "./components/ProcessingTimeSection";
import IowaCityServicesSection from "./components/ServicesSection";
import IowaCityLocalAreaSection from "./components/LocalAreaSection";
import IowaCityTestimonialsSection from "./components/TestimonialsSection";
import IowaCityAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import IowaCityComparisonSection from "./components/ComparisonSection";
import IowaCityMistakesSection from "./components/MistakesSection";
import IowaCityHowItWorksSection from "./components/HowItWorksSection";
import IowaCityFAQSection, { faqs } from "./components/FAQSection";
import IowaCityHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import IowaCityFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function IowaCityPage() {
  useEffect(() => {
    document.title = "Emergency & Expedited Passport Iowa City IA | Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/iowa-city/";

    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Iowa City, IA — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Johnson County and University of Iowa travelers.");
    setMeta("geo.placename", "Iowa City, Iowa");
    setMeta("geo.region", "US-IA");
    setMeta("geo.position", "41.6611;-91.5302");
    setMeta("ICBM", "41.6611, -91.5302");
    setMeta("keywords", "expedited passport, emergency passport, Iowa City passport, same day passport Iowa City, fast passport Iowa, University of Iowa passport, Johnson County passport");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
      { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
      { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
      { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
      { name: "Second Passport & Damaged", description: "Second passport issuance for frequent travelers and damaged passport replacement." },
    ];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Iowa", item: "https://fastpassportcenter.com/iowa/" },
          { "@type": "ListItem", position: 3, name: "Iowa City Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Fast Passport Center does not have a physical office in Iowa City — we serve this area remotely. Emergency and expedited passport services as fast as 24 hours for Iowa City and Johnson County travelers.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: {
          "@type": "City",
          name: "Iowa City",
          containedInPlace: {
            "@type": "State",
            name: "Iowa",
          },
        },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "1200",
        },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: {
          "@type": "City",
          name: "Iowa City",
        },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <IowaCityHeroSection />
      <IowaCityStickyNav />
      <IowaCityProcessingTimeSection />
      <IowaCityServicesSection />
      <IowaCityLocalAreaSection />
      <IowaCityTestimonialsSection />
      <IowaCityAcceptanceFacilitiesSection />
      <IowaCityComparisonSection />
      <IowaCityMistakesSection />
      <IowaCityHowItWorksSection />
      <IowaCityFAQSection />
      <IowaCityHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/iowa-city" state="IA" />
      <IowaCityFinalCTA />
    </div>
  );
}